import handlebarsUtils from '../handlebars-utils';
import { jQueryGetJson } from '../jquery-networking';
import debounce from '@/upload-entity-editor/utils/debounce';
import { CipCodeSearchResult } from './CipCodeSearchResult';

enum Key {
	Tab = 9,
	Enter = 13,
	Escape = 27,
	UpArrow = 38,
	DownArrow = 40,
}

const cipCodeResultsTemplate = Handlebars.compile(`
{{#if hasResults}}
	<div class="list-group">
		{{#each searchResults}}
		<a href="#" class="list-group-item list-group-item-action" data-result-index="{{@index}}">
			{{> itemTemplate}}
		</a>
		{{/each}}
	</div>
{{else if isSearchCriteriaEntered}}
	<div class="alert alert-info" role="alert">
		No CIP Codes are available based on the search criteria.
	</div>
{{else if hasError}}
	<div class="alert alert-danger" role="alert">
		An error occurred while searching.  Please check back later.
	</div>
{{else}}
	<div class="alert alert-warning" role="alert">
		Enter search criteria to see results.
	</div>
{{/if}}`);

export const defaultCipCodeItemTemplate = Handlebars.compile(`
<strong>{{cipCode}}</strong>
	<div>{{title}}</div>`);

export class CipCodeSelector {
	public searchResults: CipCodeSearchResult[] = [];
	public hasError = false;

	private cipCodeResultsTemplate: Handlebars.TemplateDelegate;
	private $input: JQuery<HTMLElement>;
	private selectedItemKey = 'selectedItem';
	private previousSearch = '';

	constructor ($textBox: JQuery<HTMLElement>, itemTemplate?: Handlebars.TemplateDelegate<any>) {
		const cipCodeItemTemplate = itemTemplate || defaultCipCodeItemTemplate;
		Handlebars.registerPartial('itemTemplate', cipCodeItemTemplate);

		this.cipCodeResultsTemplate = cipCodeResultsTemplate;
		this.$input = $textBox;

		this.init($textBox);
	}

	public get searchCriteria (): string {
		return (this.$input.val() || '').toString();
	}

	public get selectedCipCode (): CipCodeSearchResult | null {
		return this.$input.data(this.selectedItemKey);
	}

	public get isSearchCriteriaEntered (): boolean {
		return !this.hasError && this.searchCriteria !== '';
	}

	public hasResults (): boolean {
		return this.searchResults.length > 0;
	}

	public onSelected: (selected: CipCodeSearchResult) => void = (selected: CipCodeSearchResult) => undefined;

	private get isSearchCurrentlySelected (): boolean {
		const search = this.searchCriteria;
		const current = this.selectedCipCode || null;

		return (current !== null && current.entryFormat === search);
	}

	private setSelectedItem (item: CipCodeSearchResult) {
		this.$input.data(this.selectedItemKey, item);
		this.$input.val(item.entryFormat).valid();
	}

	private clearSelectedItem () {
		this.$input.data(this.selectedItemKey, null);
		// this.$input.valid();
	}

	private isChrome (): boolean {
		const chrome = (window as any).chrome;
		const test = !!chrome && (!!chrome.webstore || !!chrome.runtime);
		return test;
	}

	private closePopover () {
		this.$input.popover('hide');
		this.previousSearch = '';
	}

	private init ($textBox: JQuery<HTMLElement>) {
		const popoverCss = 'cip-selector-popover';
		const openPopover = () => $textBox.popover('show');

		const $container = $textBox.parent('.form-group');

		$textBox.attr('autocomplete', this.isChrome() ? 'new-password' : 'off');

		$container.on('focusout', (evt) => {
			const relatedTgt = evt.relatedTarget;
			let closeSelector = true;
			if (relatedTgt) {
				const $target = $(relatedTgt).closest('.form-group');
				closeSelector = $target.has($textBox[0]).length === 0;
			}

			if (closeSelector) {
				this.closePopover();
			}
		});

		$textBox.keydown((evt) => {
			const $current = $(evt.currentTarget);
			const $popover = $current.parent().find('.popover');
			const isPopoverOpen = $popover.length === 1;

			switch (evt.which) {
				case Key.Escape:
					this.closePopover();
					break;

				case Key.Tab:
				case Key.Enter:
					if (isPopoverOpen) {
						const $items = $popover.find('.list-group .list-group-item-action');

						const $first = $items.first();
						if ($items.length === 1) {
							$first.click();
						} else {
							$first.focus();
						}
						break;
					}
					return true;

				case Key.DownArrow:
					if (isPopoverOpen) {
						$popover.find('.list-group .list-group-item-action:first').focus();
						break;
					}
					return true;

				default:
					return true;
			}

			evt.preventDefault();
			return false;
		});

		$container.on('click', '.popover a.list-group-item-action', (evt) => {
			const index = $(evt.currentTarget).data('resultIndex');
			const item = this.searchResults[index];

			this.onSelected(item);
			this.setSelectedItem(item);

			this.closePopover();
			return false;
		});

		$container.on('keydown', '.popover a.list-group-item-action', (evt) => {
			const $current = $(evt.currentTarget);

			switch (evt.which) {
				case Key.Escape:
					this.closePopover();
					break;

				case Key.UpArrow:
					if ($current.index() === 0) {
						this.$input.focus();
					} else {
						$current.prev().focus();
					}
					break;

				case Key.DownArrow:
					$current.next().focus();
					break;

				default:
					return true;
			}

			evt.preventDefault();
			return false;
		});

		const loadTemplate = (template: Handlebars.TemplateDelegate): string => template(this);
		const replaceContent = (selector: string) => {
			const $resultsContainer = $(selector, $container);
			const template = this.cipCodeResultsTemplate;

			$resultsContainer.empty();
			const $updated = $(template(this));
			$resultsContainer.append($updated);
		};

		const resultsContainerId = 'search-results';
		const resultsContainerSelector = `#${resultsContainerId}`;
		const findCipCodesUrl = $textBox.data('searchAction');
		const findCipCodes = (search: string, top: number): JQueryPromise<any> => {
			const current = this.selectedCipCode;
			if (current && this.isSearchCurrentlySelected) {
				const dfd = $.Deferred();

				this.searchResults = [current];
				dfd.resolve();
				return dfd.promise();
			}

			const test = jQueryGetJson(findCipCodesUrl, { search, top })
				.then((response: any) => {
					this.searchResults = response.data.results.map((d: any) => new CipCodeSearchResult(d));
					this.hasError = false;
				});
			return test;
		};

		const performSearch = () => {
			const search = (this.searchCriteria || '').trim();
			const previous = (this.previousSearch || '').trim();

			if (search === previous) {
				return;
			}

			findCipCodes(search, 25)
				.then(() => {
					replaceContent(resultsContainerSelector);
					this.previousSearch = search;
				})
				.fail(() => {
					this.hasError = true;
					replaceContent(resultsContainerSelector);
				});
		};

		const selectedCipCodeId = ($textBox.val() || '').toString();
		if (selectedCipCodeId !== '') {
			findCipCodes(selectedCipCodeId, 1)
				.then(() => {
					this.setSelectedItem(this.searchResults[0]);
				});
		}

		$textBox.popover({
			container: $container[0],
			html: true,
			sanitize: false,
			placement: 'bottom',
			trigger: 'manual',
			content: () => {
				const $content = $('<div></div>');
				$content.attr('id', resultsContainerId);
				$content.html(loadTemplate(this.cipCodeResultsTemplate));
				return $content[0];
			}
		})
			.on('shown.bs.popover', () => {
				const $popover = $('.popover', $container);
				$popover.addClass(popoverCss);
				$popover.css('width', '95%');
				$popover.css('max-width', '100%');
				$popover.popover('update');
			})
			.on('hidden.bs.popover', () => {
				this.searchResults = [];
				this.hasError = false;
			})
			.on('keyup', debounce(() => {
				if (!this.isSearchCurrentlySelected) {
					this.clearSelectedItem();
				}
				performSearch();
			}, 300))
			.on('focus', () => {
				performSearch();
				openPopover();
			})
			.on('click', openPopover);

		const validationMethodName = 'existingCipCode';
		$.validator.addMethod(validationMethodName, (value: any, element: any) => {
			const $element = $(element);

			return $element.data(this.selectedItemKey) != null;
		}, 'The CIP Code could not be found.  Please select one from the list of available codes.');

		$textBox.attr(`data-rule-${validationMethodName}`, 'true');
	}
}
